import { Category } from '@/domain/models/content/category'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import type { RouteConfig, RoutesProps } from '../paths'

/**
 * Computes the content ID for navigation
 * Returns productId for URL generation
 *
 * @param content - Content object with id and productId
 * @returns The product ID
 */
export const computeContentValueIdHelper = (content: { id: number; productId: number }): number => {
  return content.productId
}

/**
 * Generates dynamic routes for all available categories
 *
 * @returns Object mapping category names to route configs
 */
export const getCategoriesPathsHelper = (): RoutesProps => {
  return Category.getAvailables().reduce((acc, category) => {
    return {
      ...acc,
      [`CATEGORY_${category.name.toUpperCase()}`]: {
        value: `/category/${category.name.toLocaleLowerCase()}`,
        name: category.name.toLocaleLowerCase(),
        title: category.title
      }
    }
  }, {})
}

/**
 * Generates dynamic routes for all category groups
 *
 * @returns Object mapping category group names to route configs
 */
export const getCategoryGroupPathsHelper = (): RoutesProps => {
  return EnrollmentCategory.getGroups().reduce((acc, group) => {
    return {
      ...acc,
      [`CATEGORY_GROUP_${group.name.toUpperCase()}`]: {
        value: `/category/group/${group.name.toLocaleLowerCase()}`,
        name: group.name.toLocaleLowerCase(),
        title: group.title
      }
    }
  }, {})
}

/**
 * Finds a route configuration by pathname
 *
 * @param pathname - The pathname to search for
 * @param routes - Routes object to search in
 * @returns Route config if found, null otherwise
 */
export const findRouteByPathnameHelper = (
  pathname: string,
  routes: Record<string, RouteConfig>
): RouteConfig | null => {
  let route = null
  Object.entries(routes).forEach(([key, value]) => {
    if (value.value === pathname) {
      route = routes[key]
    }
  })
  return route
}

/**
 * Finds a route configuration by category
 *
 * @param category - The category to find
 * @param routes - Routes object to search in
 * @returns Route config for the category
 */
export const findRouteByCategoryHelper = (
  category: Category.Enum,
  routes: Record<string, RouteConfig>
): RouteConfig => {
  return routes[`CATEGORY_${category.name.toUpperCase()}`]
}

/**
 * Gets the route path for a category
 *
 * @param category - The category
 * @param routes - Routes object to search in
 * @returns The lowercase route path
 */
export const getCategoryRoutePathHelper = (
  category: Category.Enum,
  routes: Record<string, RouteConfig>
): string => {
  return findRouteByCategoryHelper(category, routes).value.toLocaleLowerCase()
}

/**
 * Finds a route configuration by category group
 *
 * @param group - The category group to find
 * @param routes - Routes object to search in
 * @returns Route config for the category group
 */
export const findRouteByCategoryGroupHelper = (
  group: EnrollmentCategory.Enum,
  routes: Record<string, RouteConfig>
): RouteConfig => {
  return routes[`CATEGORY_GROUP_${group.name.toUpperCase()}`]
}

/**
 * Gets the route path for a category group
 *
 * @param group - The category group
 * @param routes - Routes object to search in
 * @returns The lowercase route path
 */
export const getCategoryGroupRoutePathHelper = (
  group: EnrollmentCategory.Enum,
  routes: Record<string, RouteConfig>
): string => {
  return findRouteByCategoryGroupHelper(group, routes).value.toLocaleLowerCase()
}

/**
 * Checks if current pathname is a static (public) route
 *
 * @param publicPaths - Public paths object to check against
 * @returns True if current path is a static route
 */
export const isInStaticRouteHelper = (publicPaths: Record<string, RouteConfig>): boolean => {
  return Object.values(publicPaths).some(route => route.value === window.location.pathname)
}
